import React, { useCallback, useMemo, useRef, useEffect, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useLocalStorage, useModal } from 'hooks';
import { Button, Card, Text } from 'v3/components';
import { Row } from 'react-bootstrap';

import { useSnackbar } from 'v3/components/Snackbar';

import {
  fetchBranchOffices,
  fetchOnlyCities,
  fetchDrivers,
  fetchGroupVehicles,
  fetchShippingCompanies,
  fetchWagons,
  onSearchNegotiator,
  fetchTags,
} from 'utils/fetches';

import {
  setCellStatus,
  getCellStatus
} from './UseCellStatusStore';

import FleetCellRenderer from './FleetCellRenderer';
import EditableHeader from './EditableHeader';
import RemoveCell from './RemoveCell';
import AsyncSelectCellEditor from './AsyncSelectCellEditor';
import SelectCellEditor from './SelectCellEditor';
import InputCellEditor from './InputCellEditor';
import ClearCellConfirmModal from './ClearCellConfirmModal';

import { validatePermission } from 'actions';
import { CLEAR_VALUE_MARKER, DEFAULT_COLUMNS, isEmptyCellValue, useFleetManagement } from '../shared';
import { CellContainer, } from '../styles';
import { GridContainer, GridViewport, StyledTablePagination } from './styles';
import { cooperplaceApi } from 'services/api';

const hasPermissionRemove = validatePermission('REMOVER_GESTAO_FROTA');

const getCellStatusKey = (rowId, field) => `${rowId}:${field}`;

// --- COMPONENTES PARA EDIÇÃO NA CÉLULA (CELL EDITORS) ---

const handleAsyncSearch = async (columnName, search) => {
  if (columnName === 'plate_wagons') return fetchWagons(search);
  if (columnName === 'driver') return fetchDrivers(search);
  if (columnName === 'operation') return fetchTags(search);
  if (columnName === 'shipment') return fetchShippingCompanies({ search });
  if (columnName === 'branch_origin') return fetchBranchOffices(search);
  if (columnName === 'operator') return onSearchNegotiator(search);
  if (columnName === 'local_availability') return fetchOnlyCities(search);
  if (columnName === 'fleet') return fetchGroupVehicles(search);
  return [];
};

// --- COMPONENTE PRINCIPAL ---

export default function FleetManagementTable() {
  const {
    fetching,
    columns,
    fleetData,
    loading,
    fetchConfig,
    deleteModal,
    setSelected,
    handleGetData,
    filters,
    suggestionsModal,
    setPerPage,
    perPage,
    setRowsPerPage,
    setIsEditing,
    refreshingGrid
  } = useFleetManagement();

  const gridRef = useRef(null);
  const saveTimersRef = useRef(new Map());
  const statusTimersRef = useRef(new Map());
  const rollbackGuardRef = useRef(new Set());
  const pendingSavesRef = useRef(new Set());
  const deleteModalRef = useRef(deleteModal);
  const suggestionsModalRef = useRef(suggestionsModal);
  const snackbar = useSnackbar();

  const [pendingClear, setPendingClear] = useState(null);
  const clearConfirmModal = useModal();

  deleteModalRef.current = deleteModal;
  suggestionsModalRef.current = suggestionsModal;

  const [columnOrder, setColumnOrder] = useLocalStorage('fleet_management_column_order', []);
  const [columnSizing, setColumnSizes] = useLocalStorage('fleet_management_column_sizes', {});

  const defaultColDef = useMemo(
    () => ({
      sortable: false,
      resizable: true,
      minWidth: 130,
      suppressHeaderMenuButton: true,
      lockPinned: true,
    }),
    [],
  );

  const updateCellStatus = useCallback((statusKey, status, timeoutMs) => {

    const previousTimer =
      statusTimersRef.current.get(statusKey);

    if (previousTimer) {
      clearTimeout(previousTimer);
      statusTimersRef.current.delete(statusKey);
    }

    setCellStatus(statusKey, status);

    if (timeoutMs) {
      const timer = setTimeout(() => {

        setCellStatus(statusKey);

        statusTimersRef.current.delete(statusKey);

      }, timeoutMs);

      statusTimersRef.current.set(statusKey, timer);
    }

  }, []);

  useEffect(() => () => {
    saveTimersRef.current.forEach((timer) => clearTimeout(timer));
    saveTimersRef.current.clear();
    statusTimersRef.current.forEach((timer) => clearTimeout(timer));
    statusTimersRef.current.clear();
    pendingSavesRef.current.clear();
    setIsEditing(false);
  }, [setIsEditing]);

  // Ponto único de salvamento: usado tanto pela edição normal (debounced) quanto
  // pela remoção explícita via ClearCellButton (imediata, com o marcador DEAL_ARG).
  const saveCellChange = useCallback(
    ({ rowId, field, newValue, oldValue, immediate, isExplicitClear }) => {
      const statusKey = getCellStatusKey(rowId, field);

      // Um valor esvaziado durante a edição normal (sem usar o ícone de limpar)
      // não deve ser salvo — só um valor novo ou a remoção explícita disparam save.
      if (isEmptyCellValue(newValue) && !isExplicitClear) {
        return;
      }

      const payload = {
        fleetRegisterId: rowId,
        columnName: field,
        newValue: isExplicitClear ? CLEAR_VALUE_MARKER : newValue,
        oldValue,
      };

      const existingSaveTimer = saveTimersRef.current.get(statusKey);
      if (existingSaveTimer) {
        clearTimeout(existingSaveTimer);
        saveTimersRef.current.delete(statusKey);
      }

      updateCellStatus(statusKey, 'pending');

      const runSave = async () => {
        try {
          await cooperplaceApi.put('fleet/management/vehicleManagement', { data: payload });

          updateCellStatus(statusKey, 'success', 5000);
        } catch (err) {
          const rowNode = gridRef.current?.api?.getRowNode(String(rowId));

          if (rowNode) {
            rollbackGuardRef.current.add(statusKey);
            rowNode.setDataValue(field, oldValue);
          }

          updateCellStatus(statusKey, 'error', 10000);
          snackbar.show(<Text> Não foi possível salvar a alteração da célula</Text>, {
            type: 'error',
          });
        } finally {
          saveTimersRef.current.delete(statusKey);
          pendingSavesRef.current.delete(statusKey);
          setIsEditing(pendingSavesRef.current.size > 0);
        }
      };

      pendingSavesRef.current.add(statusKey);
      setIsEditing(true);

      if (immediate) {
        runSave();
      } else {
        const timer = setTimeout(runSave, 500);
        saveTimersRef.current.set(statusKey, timer);
      }
    },
    [updateCellStatus, snackbar, setIsEditing],
  );

  const onCellValueChanged = useCallback(
    (event) => {
      const statusKey = getCellStatusKey(event.data.id, event.colDef.field);

      if (rollbackGuardRef.current.has(statusKey)) {
        rollbackGuardRef.current.delete(statusKey);
        return;
      }

      saveCellChange({
        rowId: event.data.id,
        field: event.colDef.field,
        newValue: event.newValue,
        oldValue: event.oldValue,
        immediate: false,
        isExplicitClear: false,
      });
    },
    [saveCellChange],
  );

  const handleRequestClearCell = useCallback(
    (rowId, field, oldValue, columnLabel) => {
      setPendingClear({ rowId, field, oldValue, columnLabel });
      clearConfirmModal.open();
    },
    [clearConfirmModal],
  );

  const handleConfirmClearCell = useCallback(() => {
    if (!pendingClear) return;

    const { rowId, field, oldValue } = pendingClear;
    const rowNode = gridRef.current?.api?.getRowNode(String(rowId));

    // Also fires onCellValueChanged with an empty newValue; the empty-value
    // guard in saveCellChange makes that call a no-op, so it's safe here.
    if (rowNode) {
      rowNode.setDataValue(field, null);
    }

    saveCellChange({
      rowId,
      field,
      newValue: null,
      oldValue,
      immediate: true,
      isExplicitClear: true,
    });

    setPendingClear(null);
    clearConfirmModal.close();
  }, [pendingClear, saveCellChange, clearConfirmModal]);

  const handleCancelClearCell = useCallback(() => {
    setPendingClear(null);
    clearConfirmModal.close();
  }, [clearConfirmModal]);

  // Mapeamento dinâmico de editores com base no tipo de componente da coluna
  const getCellEditorComponent = (component) => {
    if (component === 'input' || component === 'date') return InputCellEditor;
    if (component === 'select' || component === 'multi-select') return SelectCellEditor;
    if (component && component.includes('async')) return AsyncSelectCellEditor;
    return undefined;
  };

  const getPopUpCellEditorParams = (component) => {
    if (component === 'select' || component === 'multi-select') {
      return true
    }
    return false
  }

  // Mapeamento das Colunas do AG Grid
  const columnDefs = useMemo(() => {
    const baseCols = [
      hasPermissionRemove
        ? {
          headerName: '',
          field: 'remove',
          cellRenderer: (props) => (
            <RemoveCell
              setSelected={setSelected}
              modalHook={deleteModalRef.current}
              data={props.data}
            />
          ),
          pinned: 'left',
          lockPinned: true,
          lockPosition: 'left',
          suppressMovable: true,
          suppressSizeToFit: true,
          resizable: false,
          sortable: false,
          editable: false,
          cellStyle: { justifyContent: 'center' },
          width: 46,
          minWidth: 46,
          maxWidth: 46,
        }
        : null,

      {
        headerName: '',
        field: 'actions',
        cellRenderer: (props) => (
          <CellContainer>
            <Button
              size="xs"
              style={{ whiteSpace: 'nowrap', width: '100%', padding: '5px', margin: '0', height: '30px' }}
              onClick={(e) => {
                e.stopPropagation();
                setSelected(props.data);
                suggestionsModalRef.current.open();
              }}
            >
              Carregamentos
            </Button>
          </CellContainer>
        ),
        hide: false,
        lockVisible: true,
        suppressColumnsToolPanel: true,
        pinned: 'right',
        lockPinned: true,
        suppressMovable: true,
        lockPosition: 'right',
        suppressSizeToFit: true,
        resizable: false,
        sortable: false,
        editable: false,
        suppressSizeToFit: true,
        width: 164,
        minWidth: 164,
        maxWidth: 164,
      },
      ...columns
        .flatMap(({ items }) =>
          items.filter((item) => {
            if (DEFAULT_COLUMNS.includes(item.slug)) return true;
            return item.selected;
          }),
        )
        .map(({ name, slug, component }) => {
          const field = slug.toLowerCase();
          const editor = getCellEditorComponent(component);
          const popUpEditor = getPopUpCellEditorParams(component);
          const statusKeyGetter = (params) => getCellStatusKey(params.data.id, field);

          return {
            headerName: name,
            field,
            cellRenderer: FleetCellRenderer,
            cellDataType: false,
            headerComponent: editor ? EditableHeader : undefined,
            cellEditor: editor,
            cellEditorPopup: popUpEditor,
            cellEditorPopupPosition: popUpEditor ? 'under' : undefined,
            editable: (params) => {
              if (!editor) return false;

              const status =
                getCellStatus(
                  statusKeyGetter(params)
                );

              return status !== 'pending';
            },
            cellEditorParams: (params) => ({
              inputType: component,
              selectType: component,
              columnName: field,
              // Passa explicitamente o valor clone para o editor
              initialValue: params.value ? { ...params.value } : null,
              onSearch: (search) => handleAsyncSearch(field, search),
              getOptionLabel: (option) => option?.name || option?.plate || option?.label || option?.social_name || option?.username || option?.description || '',
              getOptionValue: (option) => option?.id || option?.wagonId || option?.value,
            }),
            cellRendererParams: {
              onClear: handleRequestClearCell,
            },

            // 3. COMPARADOR DE IGUALDADE POR ID (O PONTO CHAVE)
            equals: (valueA, valueB) => {
              if (valueA === valueB) return true;
              if (!valueA && !valueB) return true;
              if (!valueA || !valueB) return false;

              // Se ambos forem objetos com ID (operação, operador, motorista, etc.)
              if (valueA.id !== undefined && valueB.id !== undefined) {
                return String(valueA.id) === String(valueB.id);
              }

              return JSON.stringify(valueA) === JSON.stringify(valueB);
            },
          };
        })
    ].filter(Boolean);

    if (columnOrder.length > 0) {
      baseCols.sort((a, b) => {
        if (a.pinned || b.pinned) return 0;
        const indexA = columnOrder.indexOf(a.field);
        const indexB = columnOrder.indexOf(b.field);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });
    }

    return baseCols;
  }, [columns, columnSizing, columnOrder, setSelected, handleRequestClearCell]);

  const onDragStopped = useCallback(() => {
    if (!gridRef.current?.api) return;
    const allDisplayedColumns = gridRef.current.api.getAllGridColumns();
    const orderedFields = allDisplayedColumns
      .map((col) => col.getColId())
      .filter((id) => id !== 'remove' && id !== 'actions');

    setColumnOrder(orderedFields);
  }, [setColumnOrder]);

  const onColumnResized = useCallback(
    (event) => {
      if (event.finished && event.column) {
        const field = event.column.getColId();
        const newSize = event.column.getActualWidth();
        setColumnSizes((prev) => ({
          ...prev,
          [field]: newSize,
        }));
      }
    },
    [setColumnSizes],
  );

  const onGridReady = useCallback(
    (params) => {
      const columnApi = params?.columnApi || params?.api;

      if (!columnApi?.applyColumnState) return;

      const savedColumns = Object.entries(columnSizing || {}).map(([colId, width]) => ({
        colId,
        width,
      }));

      if (savedColumns.length) {
        columnApi.applyColumnState({
          state: savedColumns,
          applyOrder: false,
        });
      }
    },
    [columnSizing],
  );

  const paginationLabel = ({ from, to, count }) => {
    return `${from}-${to} de ${count}`;
  };

  const onRowsPerPageChange = useCallback(
    async ({ target: { value } }) => {
      setPerPage(value);
      await handleGetData({ ...filters, perPage: value, page: 1 });
    },
    [setPerPage, handleGetData, filters],
  );

  const onPageChange = useCallback(
    async (_, e) => {
      setRowsPerPage((prev) => ({ ...prev, page: e + 1 }));
      await handleGetData({
        ...filters,
        page: e + 1,
        perPage,
      });
    },
    [filters, handleGetData, setRowsPerPage, perPage],
  );

  return (
    <Row style={{ marginTop: 40 }}>
      <Card loading={fetching || loading} bodyCardPadding="0.5rem 1rem 1rem">
        <GridContainer>
          <GridViewport>
            <div className="ag-theme-alpine">
              <AgGridReact
                ref={gridRef}
                rowData={fleetData}
                pinnedTopRowData={
                  refreshingGrid
                    ? [{ id: '__loading__', isSkeleton: true }]
                    : []
                }
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                getRowId={(params) => String(params.data.id)}
                onCellValueChanged={onCellValueChanged}
                singleClickEdit={false}
                onGridReady={onGridReady}
                onDragStopped={onDragStopped}
                onColumnResized={onColumnResized}
                suppressDragLeaveHidesColumns={true}
                animateRows={true}
                overlayLoadingTemplate='<span class="ag-overlay-loading-center">Carregando...</span>'
                overlayNoRowsTemplate='<span>Nenhum resultado encontrado!</span>'
                headerHeight={40}
                rowHeight={40}
              />
            </div>
          </GridViewport>

          <StyledTablePagination
            rowsPerPageOptions={[
              { label: '15', value: 15 },
              { label: '30', value: 30 },
              { label: '50', value: 50 },
              { label: '100', value: 100 },
              { label: '130', value: 130 },
            ]}
            labelRowsPerPage="Registros por página:"
            rowsPerPage={perPage}
            component="div"
            count={fetchConfig?.total || 0}
            page={(fetchConfig?.page || 1) - 1}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            labelDisplayedRows={paginationLabel}
            showFirstButton
            showLastButton
          />
        </GridContainer>
      </Card>

      <ClearCellConfirmModal
        isOpen={clearConfirmModal.isOpen}
        columnLabel={pendingClear?.columnLabel}
        columnValue={pendingClear?.oldValue}
        onConfirm={handleConfirmClearCell}
        onCancel={handleCancelClearCell}
      />
    </Row>
  );
}
